import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { formatMoney } from "../../utils/formatMoney";

interface RouteFixedFare {
  id: string;
  offerId: number | null;
  offerName: string | null;
  vehicleType: string | null;
  fromLocation: string | null;
  fromPostCode: string | null;
  toLocation: string | null;
  toPostCode: string | null;
  fare: number | null;
  status: boolean;
  remarks: string | null;
}

export default function RouteFixedFareDetail() {
  const { id } = useParams();
  const [fare, setFare] = useState<RouteFixedFare | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFareData();
  }, [id]);

  const fetchFareData = async () => {
    try {
      const response = await axiosInstance.get(`/RouteFixedFare/${id}`);
      setFare(response.data);
    } catch (error) {
      setError("Failed to fetch route fixed fare data");
      console.error("Error fetching route fixed fare:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!fare) return <div>No data found</div>;

  return (
    <div className="mx-auto px-4 py-8">
      <PageBreadcrumb pageTitle="Route Fixed Fare Details"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/route-fixed-fares/${id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">From Location</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.fromLocation || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">From Postcode</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.fromPostCode || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Location</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.toLocation || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Postcode</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.toPostCode || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Vehicle Type</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.vehicleType || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Fare</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatMoney(fare.fare) !== "N/A" ? formatMoney(fare.fare) : "-"}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Offer Name</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {fare.offerName || '-'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                fare.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {fare.status ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>

          {fare.remarks && (
            <div className="col-span-2">
              <dt className="text-sm font-medium text-gray-500">Remarks</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {fare.remarks}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
} 